/**
 * Pool Reports Offhire Page
 *
 * Displays offhire reports with fuel consumption breakdown by owner
 * UI matches Angular implementation exactly
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, ExternalLink } from 'lucide-react';
import { Button, Alert, Skeleton } from '@/components/ui';
import { PoolReportTableCard } from '@/components/poolReports/PoolReportTableCard';
import {
  poolReportOwnerLabelClass,
  poolReportOwnerLinkClass,
  poolReportSelectedCellClass,
} from '@/components/poolReports/poolReportTableHighlight';
import {
  POOL_REPORT_OWNER_COL_HEADER,
  poolReportTheadMutedClass,
  poolReportTheadStickyClass,
  poolReportThClass,
  poolReportThGroupClass,
  poolReportThGroupSideClass,
} from '@/components/poolReports/poolReportTableChrome';
import { usePoolReportsStore } from '@/store';
import { useOffhires } from '@/hooks';
import { CSVService } from '@/utils/csvExport';
import { formatNumber } from '@/utils/formatters';
import type { Offhire } from '@/types/poolReports.types';

interface HiddenFuelTypes {
  HSFO: boolean;
  MOGO: boolean;
  LSFO: boolean;
  LSGO: boolean;
}

export default function PoolReportsOffhirePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { reportType, selectedReport } = usePoolReportsStore();

  const [hiddenFuelTypes, setHiddenFuelTypes] = useState<HiddenFuelTypes>({
    HSFO: false,
    MOGO: false,
    LSFO: false,
    LSGO: false,
  });
  const [highlightedRowKey, setHighlightedRowKey] = useState<string | null>(null);

  const fileId = searchParams.get('fileId');
  const poolId = searchParams.get('poolId');

  // Fetch offhires using React Query hook
  const {
    data: offhires,
    isLoading,
    error,
  } = useOffhires(fileId ? parseInt(fileId, 10) : 0, !!fileId);

  // Suppress fuel columns where total is zero (matching Angular logic)
  useEffect(() => {
    if (!offhires || offhires.length === 0) return;

    // Get the last row (total row)
    const total = offhires[offhires.length - 1];

    const hsfoTotal = (total.Fuel || 0) + (total.FuelPrice || 0) + (total.FuelSum || 0);
    const mogoTotal = (total.Diesel || 0) + (total.DieselPrice || 0) + (total.DieselSum || 0);
    const lsfoTotal = (total.LSFO || 0) + (total.LSFOPrice || 0) + (total.LSFOSum || 0);
    const lsgoTotal = (total.LSGO || 0) + (total.LSGOPrice || 0) + (total.LSGOSum || 0);

    setHiddenFuelTypes({
      HSFO: hsfoTotal === 0,
      MOGO: mogoTotal === 0,
      LSFO: lsfoTotal === 0,
      LSGO: lsgoTotal === 0,
    });
  }, [offhires]);

  const handleExportCSV = () => {
    if (!offhires || offhires.length === 0) return;

    const headers: { [key: string]: string } = {
      ownerName: '',
      vesselName: '',
      days: 'Days',
      hours: 'Hours',
      minutes: 'Minutes',
    };

    if (!hiddenFuelTypes.HSFO) {
      headers.Fuel = 'HSFO MT';
      headers.FuelPrice = 'HSFO Price $/MT';
      headers.FuelSum = 'HSFO $';
    }
    if (!hiddenFuelTypes.MOGO) {
      headers.Diesel = 'MO/GO MT';
      headers.DieselPrice = 'MO/GO Price $/MT';
      headers.DieselSum = 'MO/GO $';
    }
    if (!hiddenFuelTypes.LSFO) {
      headers.LSFO = 'LSFO MT';
      headers.LSFOPrice = 'LSFO Price $/MT';
      headers.LSFOSum = 'LSFO $';
    }
    if (!hiddenFuelTypes.LSGO) {
      headers.LSGO = 'LSGO MT';
      headers.LSGOPrice = 'LSGO Price $/MT';
      headers.LSGOSum = 'LSGO $';
    }

    headers.Lumpsum = 'Lumpsum';
    headers.Total = 'Total';

    const excludeFields = ['OwnerID', 'ReportDisplayOrder', 'VesselID', 'VesselName'];
    const heading = Object.keys(headers).filter((key) => !excludeFields.includes(key));

    const reportRows = offhires.map((item: Offhire) =>
      heading.map((key) => {
        const value = item[key as keyof Offhire];
        if (value === null || value === undefined) return '';
        if (typeof value === 'number') return formatNumber(value, 0, 0);
        return String(value);
      })
    );

    const fileName = `${selectedReport?.description || 'report'}-offhire-report`;
    CSVService.convert(
      reportRows,
      heading.map((key) => headers[key] || key)
    ).download(fileName);
  };

  const handleOwnerClick = (offhire: Offhire) => {
    if (!offhire.OwnerID) return; // Don't navigate for total row

    const route = `/reports/${reportType}/offhire/owner/${offhire.OwnerID}`;
    navigate(
      `${route}?poolId=${poolId}&fileId=${fileId}&ownerName=${encodeURIComponent(offhire.OwnerName)}`
    );
  };

  const isTotal = (offhire: Offhire) => !offhire.OwnerID;

  return (
    <div className="pool-reports-offhire-page space-y-4">
      <style>{`
        @media (min-width: 768px) {
          .pool-reports-offhire-page table.report-table th:first-child,
          .pool-reports-offhire-page table.report-table td:first-child {
            position: sticky;
            left: 0;
            min-width: 12rem;
            max-width: min(40vw, 24rem);
            border-right: solid 1px rgb(229 231 235);
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
            vertical-align: top;
            box-shadow: 2px 0 6px rgba(15, 23, 42, 0.06);
            clip-path: inset(0px -10px 0px 0px);
            z-index: 9;
          }
          .dark .pool-reports-offhire-page table.report-table th:first-child,
          .dark .pool-reports-offhire-page table.report-table td:first-child {
            border-right: solid 1px rgb(51 65 85);
            box-shadow: 2px 0 8px rgba(0, 0, 0, 0.35);
          }
          .pool-reports-offhire-page table.report-table thead th:first-child {
            z-index: 30;
            background-color: rgb(249 250 251 / 0.95);
          }
          .dark .pool-reports-offhire-page table.report-table thead th:first-child {
            background-color: rgb(15 23 42 / 0.95);
          }
        }
        @media (max-width: 767px) {
          .pool-reports-offhire-page table.report-table tbody td:first-child {
            position: static !important;
            left: auto !important;
            min-width: 11rem;
            max-width: none;
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
            overflow: visible;
            vertical-align: top;
            font-size: 0.875rem;
            line-height: 1.4;
            padding: 0.5rem 0.65rem;
            border-right: 1px solid rgb(229 231 235);
            box-shadow: none !important;
            clip-path: none !important;
          }
          .pool-reports-offhire-page table.report-table thead th:first-child {
            position: static !important;
            left: auto !important;
            min-width: 11rem;
            max-width: none;
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
            overflow: visible;
            vertical-align: top;
            border-right: 1px solid rgb(229 231 235);
            box-shadow: none !important;
            clip-path: none !important;
          }
          .dark .pool-reports-offhire-page table.report-table thead th:first-child,
          .dark .pool-reports-offhire-page table.report-table tbody td:first-child {
            border-right-color: rgb(51 65 85);
          }
        }
      `}</style>
      {/* Error Alert */}
      {error && (
        <Alert variant="error" onClose={() => {}}>
          Failed to load offhire data
        </Alert>
      )}

      <PoolReportTableCard
        eyebrow="Offhire"
        title="Owner summary"
        description="Off-hire time and bunker / fuel amounts by owner. Open linked owners for detail."
        endAdornment={
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCSV}
            leftIcon={<Download className="h-4 w-4" />}
            disabled={isLoading || !offhires || offhires.length === 0}
            className="w-full rounded-full px-4 shadow-sm sm:w-auto"
          >
            Export to Excel
          </Button>
        }
      >
        <div className="report-table-shell">
          <table className="report-table">
            {isLoading ? (
              <>
                {/* Loading skeleton */}
                <thead className={poolReportTheadMutedClass}>
                  <tr>
                    {Array.from({ length: 12 }).map((_, i) => (
                      <th key={i} className={poolReportThClass}>
                        <Skeleton className="ml-auto h-6 w-20" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
                  {Array.from({ length: 5 }).map((_, rowIdx) => (
                    <tr key={rowIdx}>
                      {Array.from({ length: 12 }).map((_, colIdx) => (
                        <td key={colIdx} className="px-2.5 py-2 sm:px-3 sm:py-2.5">
                          <Skeleton className="h-10 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </>
            ) : (
              <>
                {/* Multi-Row Header */}
                <thead className={poolReportTheadStickyClass}>
                  <tr>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      {POOL_REPORT_OWNER_COL_HEADER}
                    </th>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      DAYS
                    </th>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      HOURS
                    </th>
                    <th
                      rowSpan={2}
                      className={clsx(
                        poolReportThGroupSideClass,
                        'border-r border-gray-200 dark:border-dark-700'
                      )}
                    >
                      MINUTES
                    </th>

                    {!hiddenFuelTypes.HSFO && (
                      <th
                        colSpan={3}
                        className={clsx(
                          poolReportThGroupClass,
                          'border-l border-r border-gray-200 dark:border-dark-700'
                        )}
                      >
                        HSFO
                      </th>
                    )}

                    {!hiddenFuelTypes.MOGO && (
                      <th
                        colSpan={3}
                        className={clsx(
                          poolReportThGroupClass,
                          'border-l border-r border-gray-200 dark:border-dark-700'
                        )}
                      >
                        MO/GO
                      </th>
                    )}

                    {!hiddenFuelTypes.LSFO && (
                      <th
                        colSpan={3}
                        className={clsx(
                          poolReportThGroupClass,
                          'border-l border-r border-gray-200 dark:border-dark-700'
                        )}
                      >
                        LSFO
                      </th>
                    )}

                    {!hiddenFuelTypes.LSGO && (
                      <th
                        colSpan={3}
                        className={clsx(
                          poolReportThGroupClass,
                          'border-l border-r border-gray-200 dark:border-dark-700'
                        )}
                      >
                        LSGO
                      </th>
                    )}

                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      LUMPSUM
                    </th>
                    <th rowSpan={2} className={poolReportThGroupSideClass}>
                      TOTAL
                    </th>
                  </tr>
                  <tr>
                    {!hiddenFuelTypes.HSFO && (
                      <>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-l border-gray-200 dark:border-dark-700'
                          )}
                        >
                          MT
                        </th>
                        <th className={poolReportThGroupSideClass}>$ / MT</th>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-r border-gray-200 dark:border-dark-700'
                          )}
                        >
                          $
                        </th>
                      </>
                    )}

                    {!hiddenFuelTypes.MOGO && (
                      <>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-l border-gray-200 dark:border-dark-700'
                          )}
                        >
                          MT
                        </th>
                        <th className={poolReportThGroupSideClass}>$ / MT</th>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-r border-gray-200 dark:border-dark-700'
                          )}
                        >
                          $
                        </th>
                      </>
                    )}

                    {!hiddenFuelTypes.LSFO && (
                      <>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-l border-gray-200 dark:border-dark-700'
                          )}
                        >
                          MT
                        </th>
                        <th className={poolReportThGroupSideClass}>$ / MT</th>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-r border-gray-200 dark:border-dark-700'
                          )}
                        >
                          $
                        </th>
                      </>
                    )}

                    {!hiddenFuelTypes.LSGO && (
                      <>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-l border-gray-200 dark:border-dark-700'
                          )}
                        >
                          MT
                        </th>
                        <th className={poolReportThGroupSideClass}>$ / MT</th>
                        <th
                          className={clsx(
                            poolReportThGroupSideClass,
                            'border-r border-gray-200 dark:border-dark-700'
                          )}
                        >
                          $
                        </th>
                      </>
                    )}
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
                  {offhires?.map((offhire, idx) => {
                    const isTotalRow = isTotal(offhire);
                    const isFirstRow = idx === 0;
                    const rowKey = isTotalRow
                      ? '__total__'
                      : String(offhire.OwnerID ?? `idx-${idx}`);
                    const isHighlighted = !isTotalRow && highlightedRowKey === rowKey;
                    const hi = poolReportSelectedCellClass(isHighlighted);

                    const numCell = (extra?: string) =>
                      clsx(
                        'px-2.5 py-2 sm:px-3 sm:py-2.5 text-right text-sm tabular-nums text-gray-900 dark:text-white',
                        isFirstRow && 'border-t-2',
                        isTotalRow
                          ? 'border-t-2 border-gray-300 bg-gray-50 font-bold dark:border-dark-600 dark:bg-dark-800'
                          : 'border-t border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-900',
                        extra,
                        hi
                      );

                    const ownerCell = clsx(
                      'px-3 py-2.5 sm:px-4 sm:py-3 text-right text-sm text-gray-900 dark:text-white',
                      isFirstRow && 'border-t-2',
                      isTotalRow
                        ? 'border-t-2 border-gray-300 bg-gray-50 font-bold dark:border-dark-600 dark:bg-dark-800 md:bg-gray-50 dark:md:bg-dark-800'
                        : 'border-t border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-900 md:bg-white dark:md:bg-dark-900',
                      hi
                    );

                    const totalColCell = clsx(
                      'px-2.5 py-2 sm:px-3 sm:py-2.5 text-right tabular-nums text-gray-900 dark:text-white',
                      isFirstRow && 'border-t-2',
                      isTotalRow
                        ? 'border-t-2 border-gray-300 bg-gray-50 text-base font-bold dark:border-dark-600 dark:bg-dark-800'
                        : 'border-t border-gray-200 bg-primary-50/40 text-sm font-semibold dark:border-dark-700 dark:bg-primary-950/25 dark:text-white',
                      hi
                    );

                    return (
                      <tr
                        key={idx}
                        onClick={() => {
                          if (isTotalRow) return;
                          setHighlightedRowKey((prev) => (prev === rowKey ? null : rowKey));
                        }}
                        className={
                          isTotalRow ? '' : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-800'
                        }
                      >
                        <td className={ownerCell}>
                          {isTotalRow ? (
                            <span className={poolReportOwnerLabelClass}>{offhire.OwnerName}</span>
                          ) : (
                            <div className="flex w-full min-w-0 flex-row items-center justify-end gap-2">
                              <button
                                type="button"
                                className={clsx(
                                  poolReportOwnerLabelClass,
                                  poolReportOwnerLinkClass,
                                  'min-w-0 flex-1 text-right'
                                )}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOwnerClick(offhire);
                                }}
                              >
                                {offhire.OwnerName}
                              </button>
                              <ExternalLink
                                className="size-[1.125rem] shrink-0 text-gray-500 dark:text-gray-400"
                                aria-hidden
                              />
                            </div>
                          )}
                        </td>
                        <td className={numCell()}>{offhire.Days}</td>
                        <td className={numCell()}>{offhire.Hours}</td>
                        <td className={numCell('border-r border-gray-200 dark:border-dark-700')}>
                          {offhire.Minutes}
                        </td>

                        {/* HSFO Columns */}
                        {!hiddenFuelTypes.HSFO && (
                          <>
                            <td
                              className={numCell('border-l border-gray-200 dark:border-dark-700')}
                            >
                              {formatNumber(offhire.Fuel, 1, 1)}
                            </td>
                            <td className={numCell()}>{formatNumber(offhire.FuelPrice, 2, 2)}</td>
                            <td
                              className={numCell('border-r border-gray-200 dark:border-dark-700')}
                            >
                              {formatNumber(offhire.FuelSum, 0, 0)}
                            </td>
                          </>
                        )}

                        {/* MO/GO Columns */}
                        {!hiddenFuelTypes.MOGO && (
                          <>
                            <td
                              className={numCell('border-l border-gray-200 dark:border-dark-700')}
                            >
                              {formatNumber(offhire.Diesel, 1, 1)}
                            </td>
                            <td className={numCell()}>{formatNumber(offhire.DieselPrice, 2, 2)}</td>
                            <td
                              className={numCell('border-r border-gray-200 dark:border-dark-700')}
                            >
                              {formatNumber(offhire.DieselSum, 0, 0)}
                            </td>
                          </>
                        )}

                        {/* LSFO Columns */}
                        {!hiddenFuelTypes.LSFO && (
                          <>
                            <td
                              className={numCell('border-l border-gray-200 dark:border-dark-700')}
                            >
                              {formatNumber(offhire.LSFO, 1, 1)}
                            </td>
                            <td className={numCell()}>{formatNumber(offhire.LSFOPrice, 2, 2)}</td>
                            <td
                              className={numCell('border-r border-gray-200 dark:border-dark-700')}
                            >
                              {formatNumber(offhire.LSFOSum, 0, 0)}
                            </td>
                          </>
                        )}

                        {/* LSGO Columns */}
                        {!hiddenFuelTypes.LSGO && (
                          <>
                            <td
                              className={numCell('border-l border-gray-200 dark:border-dark-700')}
                            >
                              {formatNumber(offhire.LSGO, 1, 1)}
                            </td>
                            <td className={numCell()}>{formatNumber(offhire.LSGOPrice, 2, 2)}</td>
                            <td
                              className={numCell('border-r border-gray-200 dark:border-dark-700')}
                            >
                              {formatNumber(offhire.LSGOSum, 0, 0)}
                            </td>
                          </>
                        )}

                        <td className={numCell()}>{formatNumber(offhire.Lumpsum, 0, 0)}</td>
                        <td className={totalColCell}>{formatNumber(offhire.Total, 0, 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </>
            )}
          </table>
        </div>
      </PoolReportTableCard>

      {/* No Data State */}
      {!isLoading && (!offhires || offhires.length === 0) && !error && (
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">No offhire data available</p>
        </div>
      )}
    </div>
  );
}
